import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  generateWebPageStructuredData,
  generateBreadcrumbStructuredData,
} from "../utils/structuredData";
import { SITE_NAME, REACT_APP_SITE_URL } from "../config/seo";
import { SEO } from "../components/SEO";
import { AnimatedInView, GradientHero, GradientText, CTABand } from "../components/ui";

const PAGE_DESCRIPTION =
  "Frequently asked questions about ECR machines, POS systems, GST billing, brands, installation, and services offered by R S Enterprises in Laxmi Nagar, New Delhi.";

const faqs = [
  {
    question: "What is an ECR machine and who needs one?",
    answer:
      "An ECR (Electronic Cash Register) machine is a computerized billing device used by retailers, restaurants, and small businesses to record sales, print receipts, and manage cash transactions. Any business that handles cash sales — grocery stores, medical shops, bakeries, salons, or restaurants — benefits from an ECR machine for accurate billing and GST-compliant receipts.",
  },
  {
    question: "What POS systems do you sell?",
    answer:
      "We stock a wide range of POS (Point of Sale) systems including Essae POS 815J, 816J, 865J, 415N/416N, and 615 models, as well as WEP BP-JOY POS (Android-based). These systems come with touch screens, thermal printers, and full billing software for retail and restaurant use.",
  },
  {
    question: "Do you offer GST-compliant billing machines?",
    answer:
      "Yes. All billing machines and POS systems we sell support GST billing out of the box. They can generate GST-compliant invoices, apply multiple tax rates, and produce reports suitable for GST filing. Models like the WEP BP-JOY and WEP BP-JOY PRO are specifically designed for Indian GST requirements.",
  },
  {
    question: "Which brands do you stock?",
    answer:
      "We are authorised dealers for Essae, WEP, Trucount, NGX, Balaji, Pixel, Unisys, and Casio. This ensures you receive genuine products with full manufacturer warranty support.",
  },
  {
    question: "Do you provide installation and after-sales support?",
    answer:
      "Yes. We provide on-site installation, software configuration, and staff training as part of our service. We also offer post-installation support and repairs for all products we sell.",
  },
  {
    question: "Where are you located?",
    answer:
      "We are located at 1st Floor, Office No. 106-107, I.J Complex, G-29, Near Subhash Chowk, Opposite Metro Pillar No. 42, Laxmi Nagar, New Delhi – 110092. You can reach us on +91-9013573235 or +91-9958044400.",
  },
  {
    question: "What is the difference between an ECR machine and a POS system?",
    answer:
      "An ECR machine is a standalone billing device primarily for cash transactions and basic invoicing. A POS system is a more advanced solution that includes a computer, software, barcode scanner, receipt printer, and optionally a cash drawer — suitable for inventory management, multiple payment modes (cash, card, UPI), and detailed sales reporting. We help you choose the right solution based on your business size and needs.",
  },
  {
    question: "Can I get a product demo or site visit before purchasing?",
    answer:
      "Yes. You are welcome to visit our showroom in Laxmi Nagar, New Delhi to see live demonstrations of our products. You can also contact us to arrange a site visit for larger or customised installations. Call us at +91-9013573235 or email billingecrpos@gmail.com to schedule.",
  },
  {
    question: "Do you sell accessories like cash drawers and barcode scanners?",
    answer:
      "Yes. We stock a full range of accessories including cash drawers, MSR (Magnetic Stripe Readers), customer display units, secondary displays, and POS printers such as the Essae PR-65, PR-75, and PR-55 models.",
  },
  {
    question: "Do you offer customised POS solutions for restaurants?",
    answer:
      "Yes. We provide customised POS solutions tailored for restaurants, including table management, KOT (Kitchen Order Ticket) printing, menu billing, and multi-user logins. Our team assesses your requirements and recommends the best hardware and software combination.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

const FAQ: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <>
      <SEO
        title="FAQ"
        description={PAGE_DESCRIPTION}
        jsonLd={[
          faqJsonLd,
          generateWebPageStructuredData({
            title: `FAQ | ${SITE_NAME}`,
            description: PAGE_DESCRIPTION,
            url: `${REACT_APP_SITE_URL}/faq`,
          }),
          generateBreadcrumbStructuredData([
            { name: "Home", item: `${REACT_APP_SITE_URL}/` },
            { name: "FAQ", item: `${REACT_APP_SITE_URL}/faq` },
          ]),
        ]}
      />

      <GradientHero
        compact
        eyebrow="Help Center"
        title={
          <>
            Frequently asked <GradientText>questions</GradientText>
          </>
        }
        subtitle="Everything you need to know about ECR machines, POS systems, and our services."
      />

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <AnimatedInView>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              disableGutters
              sx={{
                mb: 1.5,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
                "&.Mui-expanded": {
                  borderColor: "secondary.main",
                  boxShadow: "0 8px 24px rgba(199,93,44,0.1)",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "secondary.main" }} />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{ py: 0.5 }}
              >
                <Typography variant="subtitle1" fontWeight={650} component="h2">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </AnimatedInView>

        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Still have questions?{" "}
            <Link component={RouterLink} to="/contact" sx={{ fontWeight: 600, color: "secondary.dark" }}>
              Contact us
            </Link>{" "}
            and we'll be happy to help.
          </Typography>
        </Box>
      </Container>

      <CTABand
        title="Talk to a billing expert"
        subtitle="Get honest advice on the right ECR or POS setup for your business — no obligation."
      />
    </>
  );
};

export default FAQ;
